import { COORDINATE_KEY_CODE, WHEEL_KEY_CODE, type MacroEditorAction } from '../types'

/** 与 SDK encodeMacroData 一致：2 字节总长度 + 2 字节循环次数。 */
export const MACRO_ENCODED_HEADER_BYTE_LENGTH = 4

/** 设备宏写入的单包长度。短尾包如果刚好把槽位写满，设备会拒绝这一包。 */
export const MACRO_DEVICE_PACKET_BYTE_LENGTH = 24

/**
 * 协议上限里、这次写入实际能写完的字节数。
 * 上限能被 24 整除时，最后一包是满包，可以写到上限本身。
 * 不能整除时，写到上限的那一包是短包，结束地址等于槽位末尾，设备会拒绝。
 * 可写长度停在上一满包：1024 → 1008。刚好不超过这个值的内容不截。
 */
export function getDeviceWritableMacroByteLength(maxByteLength: number): number {
  if (!Number.isFinite(maxByteLength) || maxByteLength <= 0) return maxByteLength
  const remainder = maxByteLength % MACRO_DEVICE_PACKET_BYTE_LENGTH
  return remainder === 0 ? maxByteLength : maxByteLength - remainder
}

/**
 * 保留编码后仍能写入设备的前缀动作。
 * 某个动作加上后仍不超过可写长度（包括刚好相等）就保留；放不下时从该动作起整段去掉，不拆开动作。
 */
export function takeActionsWithinMacroByteLength<T>(
  actions: readonly T[],
  maxByteLength: number,
  getByteLength: (action: T) => number,
): { actions: T[]; truncated: boolean } {
  const limit = getDeviceWritableMacroByteLength(maxByteLength)
  const kept: T[] = []
  let used = MACRO_ENCODED_HEADER_BYTE_LENGTH

  for (const action of actions) {
    const size = getByteLength(action)
    if (used + size > limit) {
      return { actions: kept, truncated: true }
    }
    kept.push(action)
    used += size
  }

  return { actions: kept, truncated: false }
}

/**
 * 单个动作写入固件时的字节数，对齐 encodeMacroData：
 * 普通按键 3、坐标 0xF7 为 6、滚轮 0xF8 为 10。
 */
export function getMacroActionEncodedByteLength(key: number): number {
  if (key === COORDINATE_KEY_CODE) return 6
  if (key === WHEEL_KEY_CODE) return 10
  return 3
}

/** 整条宏编码后的总字节数（含 4 字节头部）。延迟附在前一个动作上，不额外占字节。 */
export function getEncodedMacroByteLength(actions: Array<{ key: number }>): number {
  return actions.reduce(
    (total, action) => total + getMacroActionEncodedByteLength(action.key),
    MACRO_ENCODED_HEADER_BYTE_LENGTH,
  )
}

/**
 * 编辑器卡片对应的固件编码字节数。
 * 延迟卡片不占字节；滚轮按 10 字节计，不能当成普通按键 3 字节。
 */
export function getEditorRecordEncodedByteLength(record: MacroEditorAction): number {
  if (record.type === 4) return 0
  if (record.type === 2) return getMacroActionEncodedByteLength(COORDINATE_KEY_CODE)
  if (record.type === 3) return getMacroActionEncodedByteLength(WHEEL_KEY_CODE)
  return getMacroActionEncodedByteLength(record.keystroke)
}
